import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Sparkles, ShieldCheck } from 'lucide-react';
import IdentityQuiz from '@/components/stages/IdentityQuiz';
import { GRAND_REVEAL } from '@/config/missionContent';

/* ═══════════════════════════════════════════════
   BOKEH LIGHTS — warm floating orbs of light
   ═══════════════════════════════════════════════ */
function BokehLights() {
  const orbs = useMemo(() =>
    Array.from({ length: 35 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 40 + Math.random() * 140,
      color: [
        'rgba(244,114,182,0.12)',
        'rgba(251,191,36,0.10)',
        'rgba(196,181,253,0.10)',
        'rgba(252,165,165,0.08)',
        'rgba(253,224,71,0.07)',
        'rgba(249,168,212,0.10)',
        'rgba(167,139,250,0.08)',
      ][Math.floor(Math.random() * 7)],
      duration: 8 + Math.random() * 14,
      delay: Math.random() * 5,
      driftX: (Math.random() - 0.5) * 40,
      driftY: (Math.random() - 0.5) * 25,
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {orbs.map(o => (
        <motion.div
          key={o.id}
          className="absolute rounded-full"
          style={{
            left: `${o.x}%`,
            top: `${o.y}%`,
            width: o.size,
            height: o.size,
            background: o.color,
            filter: `blur(${o.size * 0.4}px)`,
          }}
          animate={{
            x: [0, o.driftX, -o.driftX * 0.5, 0],
            y: [0, o.driftY, -o.driftY * 0.7, 0],
            opacity: [0.6, 1, 0.7, 0.6],
            scale: [1, 1.1, 0.95, 1],
          }}
          transition={{
            duration: o.duration,
            delay: o.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FLOATING HEARTS — gentle hearts drifting upward
   ═══════════════════════════════════════════════ */
function FloatingHearts() {
  const hearts = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      size: 8 + Math.random() * 14,
      duration: 12 + Math.random() * 10,
      delay: Math.random() * 15,
      drift: (Math.random() - 0.5) * 60,
      opacity: 0.15 + Math.random() * 0.25,
      color: ['#f472b6', '#fb7185', '#f9a8d4', '#fda4af', '#ec4899'][Math.floor(Math.random() * 5)],
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[2] overflow-hidden">
      {hearts.map(h => (
        <motion.div
          key={h.id}
          className="absolute"
          style={{ left: `${h.x}%`, bottom: -30 }}
          animate={{
            y: [0, '-110vh'],
            x: [0, h.drift, -h.drift * 0.4, h.drift * 0.6],
            rotate: [0, h.drift > 0 ? 20 : -20, 0],
            opacity: [0, h.opacity, h.opacity, 0],
          }}
          transition={{
            duration: h.duration,
            delay: h.delay,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        >
          <svg width={h.size} height={h.size} viewBox="0 0 20 20">
            <path d="M10 18 C5 13, 0 8, 0 5 C0 2, 2 0, 5 0 C7 0, 9 1.5, 10 3 C11 1.5, 13 0, 15 0 C18 0, 20 2, 20 5 C20 8, 15 13, 10 18Z" fill={h.color} />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SHOOTING STARS — occasional streaks
   ═══════════════════════════════════════════════ */
function ShootingStars() {
  const stars = useMemo(() =>
    Array.from({ length: 5 }, (_, i) => ({
      id: i,
      startX: 10 + Math.random() * 60,
      startY: Math.random() * 40,
      angle: 15 + Math.random() * 30,
      length: 80 + Math.random() * 120,
      duration: 0.8 + Math.random() * 0.6,
      delay: 3 + i * 4 + Math.random() * 6,
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[3] overflow-hidden">
      {stars.map(s => (
        <motion.div
          key={s.id}
          className="absolute"
          style={{
            left: `${s.startX}%`,
            top: `${s.startY}%`,
            width: s.length,
            height: 1.5,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), rgba(251,191,36,0.4))',
            borderRadius: 2,
            transform: `rotate(${s.angle}deg)`,
            transformOrigin: 'left center',
          }}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: [0, 1, 1, 0], opacity: [0, 0.8, 0.6, 0], x: [0, s.length * 0.7] }}
          transition={{
            duration: s.duration,
            delay: s.delay,
            repeat: Infinity,
            repeatDelay: 8 + Math.random() * 12,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FALLING ROSE PETALS
   ═══════════════════════════════════════════════ */
function RosePetals() {
  const petals = useMemo(() =>
    Array.from({ length: 22 }, (_, i) => ({
      id: i,
      x: Math.random() * 110 - 5,
      size: 8 + Math.random() * 14,
      duration: 10 + Math.random() * 8,
      delay: Math.random() * 12,
      sway: 30 + Math.random() * 50,
      rotation: Math.random() * 720 - 360,
      color: [
        '#fda4af', '#fb7185', '#f472b6', '#e879f9', '#fca5a5', '#fecdd3',
      ][Math.floor(Math.random() * 6)],
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-[4]">
      {petals.map(p => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.x}%` }}
          initial={{ y: -30, rotate: 0, opacity: 0 }}
          animate={{
            y: ['0vh', '110vh'],
            x: [0, p.sway, -p.sway * 0.5, p.sway * 0.3],
            rotate: p.rotation,
            opacity: [0, 0.7, 0.7, 0],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <svg width={p.size} height={p.size * 1.3} viewBox="0 0 20 26">
            <path
              d="M10 0 C5 5, 0 12, 2 18 C4 24, 10 26, 10 26 C10 26, 16 24, 18 18 C20 12, 15 5, 10 0Z"
              fill={p.color}
              opacity="0.6"
            />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SPARKLE FIELD — twinkling background sparkles
   ═══════════════════════════════════════════════ */
function SparkleField() {
  const sparkles = useMemo(() =>
    Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 3 + Math.random() * 3,
      size: 3 + Math.random() * 5,
      color: ['#fbbf24', '#f9a8d4', '#c4b5fd', '#fde68a'][Math.floor(Math.random() * 4)],
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[5] overflow-hidden">
      {sparkles.map(s => (
        <motion.div
          key={s.id}
          className="absolute"
          style={{ left: `${s.x}%`, top: `${s.y}%` }}
          animate={{
            scale: [0, 1.2, 0],
            opacity: [0, 1, 0],
            rotate: [0, 90],
          }}
          transition={{
            duration: s.duration,
            delay: s.delay,
            repeat: Infinity,
            repeatDelay: 2 + Math.random() * 4,
          }}
        >
          <svg width={s.size * 2} height={s.size * 2} viewBox="0 0 20 20">
            <path d="M10 0 L11.5 8.5 L20 10 L11.5 11.5 L10 20 L8.5 11.5 L0 10 L8.5 8.5Z" fill={s.color} opacity="0.8" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

/* ─── CONSOLE LINES — cute animated terminal log ─── */
const CONSOLE_LINES = GRAND_REVEAL.consoleLines;

function ConsoleLines({ elapsed }) {
  return (
    <>
      {CONSOLE_LINES.map((line, i) => {
        if (elapsed < line.t) return null;
        const age = elapsed - line.t;
        const isNew = age < 0.6;

        if (line.special === 'ready') {
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="pt-4 mt-3"
              style={{ borderTop: '1px solid rgba(244,114,182,0.2)' }}
            >
              <span className="text-base" style={{ color: '#4ade80' }}>{'>'} </span>
              <span className="text-base font-bold" style={{ color: '#4ade80', textShadow: '0 0 12px rgba(74,222,128,0.4)' }}>
                {GRAND_REVEAL.console.ready}
              </span>
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.6, repeat: Infinity }}
                className="text-base"
                style={{ color: '#4ade80' }}
              > █</motion.span>
            </motion.div>
          );
        }

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: isNew ? [0, 1] : 0.45, x: 0 }}
            transition={{ duration: 0.35 }}
            className="py-[3px]"
          >
            <span className="text-xs sm:text-xs" style={{ color: 'rgba(244,114,182,0.25)' }}>
              [{String(Math.floor(line.t / 60)).padStart(2, '0')}:{(line.t % 60).toFixed(1).padStart(4, '0')}]{' '}
            </span>
            <span className="text-sm sm:text-base" style={{ color: line.color, opacity: 0.6 }}>{line.icon} </span>
            <span className="text-sm sm:text-base" style={{ color: line.color }}>{line.text}</span>
          </motion.div>
        );
      })}
    </>
  );
}

/* ─── HUD top bar ─── */
function GrandRevealHUD() {
  return (
    <div className="max-w-3xl mx-auto mb-2 relative z-20 px-4 pt-4">
      <div className="bg-black/50 border border-pink-900/30 rounded px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" />
            <span className="text-xs font-terminal text-pink-700/60 tracking-[3px]">
              {GRAND_REVEAL.checkpoint}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Sparkles size={9} className="text-amber-400/60" />
            <span className="text-xs font-terminal text-amber-400/60 tracking-wider">
              {GRAND_REVEAL.status}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   GRAND REVEAL — 15s cinematic transition screen
   ═══════════════════════════════════════════════ */
export default function GrandReveal({ onComplete }) {
  const [phase, setPhase] = useState('transition'); // transition | content | quiz
  const [showButton, setShowButton] = useState(false);
  const [progress, setProgress] = useState(0);
  const [consoleElapsed, setConsoleElapsed] = useState(0);

  const handleContinueClick = useCallback(() => setPhase('quiz'), []);

  /* ─── Phase auto-advance: transition → content at 5s ─── */
  useEffect(() => {
    if (phase !== 'transition') return;
    const t = setTimeout(() => setPhase('content'), 5000);
    return () => clearTimeout(t);
  }, [phase]);

  /* ─── Console timer: starts only when content phase begins ─── */
  useEffect(() => {
    if (phase !== 'content') return;
    const DURATION = 15; // seconds for console to complete
    const start = Date.now();
    let raf;
    const tick = () => {
      const sec = (Date.now() - start) / 1000;
      const clamped = Math.min(sec, DURATION);
      setConsoleElapsed(clamped);
      setProgress((clamped / DURATION) * 100);
      if (clamped < DURATION) raf = requestAnimationFrame(tick);
      else setShowButton(true);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  return (
    <div className="min-h-screen relative" data-testid="grand-reveal" style={{
      background: 'linear-gradient(160deg, #0c0015 0%, #110520 25%, #0d0a1a 50%, #0a0510 75%, #08000f 100%)',
    }}>
      <BokehLights />
      <SparkleField />
      <FloatingHearts />
      <ShootingStars />
      <RosePetals />

      {/* ── TRANSITION PHASE: Tactical → Romantic ── */}
      <AnimatePresence>
        {phase === 'transition' && (
          <motion.div
            key="transition"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden"
            style={{ background: '#050a05' }}
          >
            {/* Fading green tactical grid */}
            <motion.div
              initial={{ opacity: 0.06 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 3, delay: 0.5 }}
              className="absolute inset-0"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(74,222,128,0.3) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(74,222,128,0.3) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
              }}
            />

            {/* Green-to-pink radial transition */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.4, 0.6] }}
              transition={{ duration: 3, delay: 1.5 }}
              className="absolute inset-0"
              style={{ background: 'radial-gradient(circle at 50% 50%, rgba(244,114,182,0.15), rgba(167,139,250,0.08) 40%, transparent 70%)' }}
            />

            {/* Shockwave pulse */}
            <motion.div
              initial={{ scale: 0, opacity: 0.6 }}
              animate={{ scale: [0, 3, 5], opacity: [0.6, 0.2, 0] }}
              transition={{ duration: 2.5, ease: 'easeOut' }}
              className="absolute rounded-full"
              style={{ width: 200, height: 200, border: '1.5px solid rgba(34,197,94,0.3)' }}
            />
            <motion.div
              initial={{ scale: 0, opacity: 0.4 }}
              animate={{ scale: [0, 3, 5], opacity: [0.4, 0.15, 0] }}
              transition={{ duration: 2.5, delay: 0.3, ease: 'easeOut' }}
              className="absolute rounded-full"
              style={{ width: 200, height: 200, border: '1.5px solid rgba(244,114,182,0.2)' }}
            />

            {/* Central content */}
            <div className="text-center relative z-10">
              {/* Shield icon morphing to heart */}
              <motion.div className="mb-8 relative inline-block">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: [0, 1.2, 1], rotate: 0 }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <motion.div
                    animate={{
                      boxShadow: [
                        '0 0 30px rgba(34,197,94,0.2)',
                        '0 0 50px rgba(244,114,182,0.3)',
                        '0 0 40px rgba(244,114,182,0.2)',
                      ],
                    }}
                    transition={{ duration: 3, delay: 1 }}
                    className="w-24 h-24 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(34,197,94,0.08)', border: '2px solid rgba(34,197,94,0.3)' }}
                  >
                    <motion.div
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 0 }}
                      transition={{ duration: 0.5, delay: 2.5 }}
                    >
                      <ShieldCheck size={38} style={{ color: '#4ade80' }} />
                    </motion.div>
                    <motion.div
                      className="absolute"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 2.5, type: 'spring', stiffness: 200 }}
                    >
                      <Heart size={34} fill="currentColor" style={{ color: '#f472b6' }} />
                    </motion.div>
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* "MISSION COMPLETE" text */}
              <motion.p
                initial={{ opacity: 0, letterSpacing: '0.8em' }}
                animate={{ opacity: 1, letterSpacing: '0.35em' }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-2xl md:text-3xl font-bold tracking-[0.35em] mb-3 font-orbitron"
                style={{ color: '#4ade80', textShadow: '0 0 30px rgba(34,197,94,0.35)' }}
              >
                {GRAND_REVEAL.transition.heading}
              </motion.p>

              {/* Color-shifting line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="w-40 h-px mx-auto mb-5"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(34,197,94,0.5), transparent)', transformOrigin: 'center' }}
              />

              {/* Typewriter status messages */}
              <div className="h-6 mb-4">
                {GRAND_REVEAL.transition.messages.map((msg, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: [0, 1, 1, 0], x: 0 }}
                    transition={{ delay: msg.delay, duration: 0.8, times: [0, 0.1, 0.8, 1] }}
                    className="text-xs tracking-wider absolute left-0 right-0 font-terminal"
                    style={{ color: i >= 3 ? 'rgba(244,114,182,0.5)' : `rgba(74,222,128,${0.5 - i * 0.1})` }}
                  >
                    {msg.text}
                  </motion.p>
                ))}
              </div>

              {/* Final warm pulse before exit */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.3, 0] }}
                transition={{ duration: 1, delay: 3.5 }}
                className="fixed inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(244,114,182,0.2), transparent 60%)' }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── QUIZ PHASE ── */}
      <AnimatePresence>
        {phase === 'quiz' && (
          <motion.div
            key="quiz"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10"
          >
            <IdentityQuiz onComplete={onComplete} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── MAIN CONTENT (after transition) ── */}
      <AnimatePresence>
        {phase === 'content' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }} className="relative z-10">

            <GrandRevealHUD />

            {/* ── CONSOLE LOADING TERMINAL ── */}
            <div className="flex items-center justify-center pt-10 sm:pt-16 pb-10 px-3 sm:px-6 relative min-h-[70vh]">
              {/* Breathing glow behind terminal */}
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.35, 0.15] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
                style={{ width: 800, height: 500, background: 'radial-gradient(ellipse, rgba(244,114,182,0.18), rgba(167,139,250,0.1) 40%, transparent 70%)', filter: 'blur(80px)' }}
              />

              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="w-full max-w-2xl relative z-10"
              >
                {/* Terminal window */}
                <div className="rounded-2xl overflow-hidden" style={{
                  background: 'rgba(8,3,18,0.92)',
                  border: '1px solid rgba(244,114,182,0.2)',
                  boxShadow: '0 25px 80px rgba(0,0,0,0.6), 0 0 60px rgba(244,114,182,0.08), inset 0 1px 0 rgba(255,255,255,0.03)',
                }}>
                  {/* Title bar */}
                  <div className="flex items-center justify-between px-5 py-3" style={{
                    background: 'linear-gradient(180deg, rgba(244,114,182,0.08), rgba(244,114,182,0.03))',
                    borderBottom: '1px solid rgba(244,114,182,0.12)',
                  }}>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: '#f472b6', boxShadow: '0 0 6px rgba(244,114,182,0.4)' }} />
                        <div className="w-3 h-3 rounded-full" style={{ background: '#fbbf24', boxShadow: '0 0 6px rgba(251,191,36,0.4)' }} />
                        <div className="w-3 h-3 rounded-full" style={{ background: '#4ade80', boxShadow: '0 0 6px rgba(74,222,128,0.4)' }} />
                      </div>
                      <span className="text-xs sm:text-xs font-terminal tracking-[0.2em] ml-1" style={{ color: 'rgba(244,114,182,0.5)' }}>
                        {GRAND_REVEAL.console.programName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <motion.div
                        animate={{ opacity: [0.3, 0.8, 0.3] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-2 h-2 rounded-full"
                        style={{ background: showButton ? '#4ade80' : '#fbbf24', boxShadow: showButton ? '0 0 8px rgba(74,222,128,0.5)' : '0 0 8px rgba(251,191,36,0.5)' }}
                      />
                      <span className="text-xs font-terminal tracking-wider" style={{ color: 'rgba(251,191,36,0.4)' }}>
                        {showButton ? GRAND_REVEAL.console.complete : GRAND_REVEAL.console.running}
                      </span>
                    </div>
                  </div>

                  {/* Terminal body */}
                  <div className="p-5 sm:p-7 font-terminal leading-relaxed min-h-[340px] sm:min-h-[400px]">
                    <ConsoleLines elapsed={consoleElapsed} />
                  </div>

                  {/* Bottom bar with progress */}
                  <div className="px-5 py-2.5" style={{ borderTop: '1px solid rgba(244,114,182,0.08)', background: 'rgba(244,114,182,0.02)' }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-terminal tracking-wider" style={{ color: 'rgba(244,114,182,0.3)' }}>
                        {GRAND_REVEAL.console.progress}
                      </span>
                      <span className="text-xs font-terminal tracking-wider" style={{ color: 'rgba(244,114,182,0.3)' }}>
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="h-[3px] rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(244,114,182,0.08)' }}>
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: 'linear-gradient(90deg, #ec4899, #f9a8d4, #fbbf24)', boxShadow: '0 0 8px rgba(244,114,182,0.3)' }}
                        initial={{ width: '0%' }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3, ease: 'linear' }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* ── PROCEED BUTTON ── */}
            <AnimatePresence>
              {showButton && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                  className="text-center mt-12 relative z-20"
                >
                  <button
                    onClick={handleContinueClick}
                    className="inline-flex items-center gap-3 px-10 py-4 rounded-2xl font-rajdhani text-base font-medium text-white transition-all duration-300 hover:scale-105 active:scale-95"
                    style={{
                      background: 'linear-gradient(135deg, #ec4899, #f9a8d4)',
                      boxShadow: '0 8px 30px rgba(244,114,182,0.3)',
                    }}
                  >
                    <Heart size={18} fill="#fff" strokeWidth={0} />
                    {GRAND_REVEAL.continueButton}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
